import { useEffect, useRef, useState, type FormEvent, type PointerEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { stopRobots } from '../api/queries';
import type { Lang } from '../api/types';
import { IconBack, IconMic } from '../components/Icon';
import { Battery, Modal } from '../components/ui';
import { RobotAudio, RobotVideo } from '../components/Video';
import { toastErr } from '../components/toast';
import { useT } from '../i18n';
import { useMedia, useWatch } from '../live/media';
import { useLive, useRobotList } from '../live/store';
import { MAX_W, useDriveLoop } from '../lib/drive';
import { useMediaQuery, useStoredState } from '../lib/hooks';
import { LANGS, sendCmd } from '../lib/robot';
import { lastFocus, rememberFocus } from '../lib/selection';
import { talk } from '../lib/talk';

const R = 74; // knob travel radius: (240 - 92) / 2
const DEAD = 0.12;

/** Phone drive screen (PhoneDrive.dc.html): video, virtual joystick → v/w, hold-to-talk, big STOP. */
export default function PhoneDrive() {
  const { serial = '' } = useParams();
  const t = useT();
  const robot = useLive((s) => s.robots[serial]);
  const info = useMedia((s) => s.byRobot[serial]);
  const [vMax, setVMax] = useStoredState('cruzr.phoneVMax', 0.6);
  const { set, active } = useDriveLoop(serial);
  const [knob, setKnob] = useState<[number, number]>([0, 0]);
  const [grabbing, setGrabbing] = useState(false);
  const [sayOpen, setSayOpen] = useState(false);
  const center = useRef<[number, number] | null>(null);
  useWatch(serial, Boolean(robot?.online));
  useEffect(() => {
    if (serial) rememberFocus(serial);
    return () => void talk(serial, false);
  }, [serial]);

  if (!robot) return <div className="empty">{t('common.loading')}</div>;
  const online = robot.online;

  const move = (e: PointerEvent<HTMLDivElement>) => {
    if (!center.current) return;
    let dx = e.clientX - center.current[0];
    let dy = e.clientY - center.current[1];
    const d = Math.hypot(dx, dy);
    if (d > R) {
      dx = (dx / d) * R;
      dy = (dy / d) * R;
    }
    setKnob([dx, dy]);
    const fy = -dy / R;
    const fx = -dx / R;
    const v = Math.abs(fy) < DEAD ? 0 : fy * vMax;
    const w = Math.abs(fx) < DEAD ? 0 : fx * MAX_W * 0.75;
    set(v || w ? { v, w } : null);
  };
  const end = () => {
    center.current = null;
    setGrabbing(false);
    setKnob([0, 0]);
    set(null);
  };
  const stop = () => {
    end();
    stopRobots([serial]).catch((e) => toastErr(e));
  };

  return (
    <div className="phone">
      <header className="phone-header">
        <Link to={`/robots/${encodeURIComponent(serial)}`} className="btn icon sm ghost" aria-label={t('common.back')}>
          <IconBack />
        </Link>
        <h1>{robot.name}</h1>
        <span className={`dot ${online ? 'green' : ''}`} />
        <Battery pct={robot.status?.battery} charging={robot.status?.charging} />
      </header>
      <div className="phone-video">
        {online ? <RobotVideo serial={serial} online placeholder={t('robot.headCamera')} /> : <span className="video-state">{t('media.offline')}</span>}
        <RobotAudio serial={serial} enabled />
        {online && (
          <button
            type="button"
            className={`overlay-btn${info?.talking ? ' on' : ''}`}
            style={{ position: 'absolute', right: 12, bottom: 12, fontWeight: 600, zIndex: 3, touchAction: 'none' }}
            disabled={info?.state !== 'connected'}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              void talk(serial, true);
            }}
            onPointerUp={() => void talk(serial, false)}
            onPointerCancel={() => void talk(serial, false)}
            onContextMenu={(e) => e.preventDefault()}
          >
            <IconMic />
            {info?.talking ? t('phone.talking') : t('phone.holdToTalk')}
          </button>
        )}
      </div>
      <div className="phone-body">
        <div
          className="joystick"
          aria-label={t('phone.joystick')}
          role="application"
          style={{ opacity: online ? 1 : 0.5 }}
          onPointerDown={(e) => {
            if (!online) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            const r = e.currentTarget.getBoundingClientRect();
            center.current = [r.left + r.width / 2, r.top + r.height / 2];
            setGrabbing(true);
            move(e);
          }}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onLostPointerCapture={end}
          onContextMenu={(e) => e.preventDefault()}
        >
          <span className="lbl" style={{ top: 14 }}>
            {t('phone.forward')}
          </span>
          <span className="lbl" style={{ bottom: 14 }}>
            {t('phone.back')}
          </span>
          <span className="lbl" style={{ left: 14 }}>
            {t('phone.left')}
          </span>
          <span className="lbl" style={{ right: 14 }}>
            {t('phone.right')}
          </span>
          <div className="knob" style={{ transform: `translate(${knob[0]}px, ${knob[1]}px)`, transition: grabbing ? 'none' : 'transform .12s ease-out' }} />
        </div>
        <span className="mono" style={{ fontSize: 13, color: 'var(--text-2)' }}>
          v {(active?.v ?? 0).toFixed(2)} m/s · w {Math.round(active?.w ?? 0)} °/s
        </span>
        <label className="range-field" style={{ width: '100%', maxWidth: 420 }}>
          <span className="top">
            {t('phone.maxSpeed')} <span className="val">{vMax.toFixed(2)} m/s</span>
          </span>
          <input type="range" min={0.1} max={1} step={0.05} value={vMax} onChange={(e) => setVMax(Number(e.target.value))} />
        </label>
      </div>
      <div className="phone-footer">
        <button type="button" className="btn xl" style={{ flexGrow: 1, background: 'var(--panel)' }} disabled={!online} onClick={() => setSayOpen(true)}>
          {t('phone.say')}
        </button>
        <button type="button" className="btn xl danger" style={{ flexGrow: 2, fontSize: 16, fontWeight: 700, letterSpacing: '0.06em' }} onClick={stop}>
          {t('drive.stop')}
        </button>
      </div>
      {sayOpen && <SayModal serial={serial} onClose={() => setSayOpen(false)} />}
    </div>
  );
}

function SayModal({ serial, onClose }: { serial: string; onClose: () => void }) {
  const t = useT();
  const [lang, setLang] = useStoredState<Lang>('cruzr.sayLang', 'ru');
  const [text, setText] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    void sendCmd(serial, 'say', { text: text.trim(), lang });
    onClose();
  };
  return (
    <Modal title={t('phone.sayTitle')} onClose={onClose}>
      <form className="col" onSubmit={submit} style={{ gap: 12 }}>
        <div role="radiogroup" aria-label={t('speech.language')} className="seg sm" style={{ alignSelf: 'flex-start' }}>
          {LANGS.map((l) => (
            <button key={l.id} type="button" role="radio" aria-checked={lang === l.id} onClick={() => setLang(l.id)}>
              {l.label}
            </button>
          ))}
        </div>
        <textarea className="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('speech.placeholder')} />
        <div className="dialog-actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn primary">
            {t('speech.say')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** /drive: open the last controlled robot (phone drive on small screens), or pick one. */
export function DrivePicker() {
  const t = useT();
  const robots = useRobotList();
  const mobile = useMediaQuery('(max-width: 480px)');
  const nav = useNavigate();
  const last = lastFocus();
  const target = (last && robots.find((r) => r.serial === last)) || robots.find((r) => r.online);
  if (target) return <Navigate to={`/${mobile ? 'drive' : 'robots'}/${encodeURIComponent(target.serial)}`} replace />;
  return (
    <div className="col" style={{ padding: 20 }}>
      <h1 className="page-title">{t('nav.drive')}</h1>
      {robots.length ? (
        robots.map((r) => (
          <button key={r.serial} type="button" className="list-row" onClick={() => nav(`/robots/${encodeURIComponent(r.serial)}`)}>
            <span className={`dot ${r.online ? 'green' : ''}`} />
            {r.name}
          </button>
        ))
      ) : (
        <div className="empty">{t('fleet.empty')}</div>
      )}
    </div>
  );
}
